<script setup>
// Grade de horários da agenda (ver AGENDA.md): uma coluna por profissional (dia) ou por dia (semana).
// Cada coluna traz, em minutos do dia: períodos de atendimento, bloqueios e consultas.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  minutesToTime,
  assignLanes,
  snapMinute,
} from '../helpers/agendaTime';

const props = defineProps({
  // [{ key, title, subtitle, color, date, periods, snapPeriods, duration, blocks, appointments }]
  columns: { type: Array, required: true },
  startMinute: { type: Number, required: true },
  endMinute: { type: Number, required: true },
  today: { type: String, default: '' },
  nowMinute: { type: Number, default: -1 },
  showProfessionalName: { type: Boolean, default: false },
  columnMinWidth: { type: String, default: '11rem' },
});

const emit = defineEmits(['selectSlot', 'openAppointment']);

const { t } = useI18n();

const PX_PER_MINUTE = 1.6;
// Cartões curtos: até 40px uma linha (horário + nome); até 64px duas linhas; acima, três.
const ONE_LINE_PX = 40;
const TWO_LINES_PX = 64;
const hover = ref(null);

const height = computed(
  () => (props.endMinute - props.startMinute) * PX_PER_MINUTE
);
const hours = computed(() => {
  const list = [];
  for (let m = props.startMinute; m < props.endMinute; m += 60) list.push(m);
  return list;
});

const top = minute =>
  `${(Math.max(minute, props.startMinute) - props.startMinute) * PX_PER_MINUTE}px`;
const span = (start, end) =>
  `${(Math.min(end, props.endMinute) - Math.max(start, props.startMinute)) * PX_PER_MINUTE}px`;

const cardLines = item => {
  const px = (item.end - item.start) * PX_PER_MINUTE;
  if (px < ONE_LINE_PX) return 1;
  return px < TWO_LINES_PX ? 2 : 3;
};

const laidOut = column =>
  assignLanes(column.appointments).map(item => ({
    ...item,
    lines: cardLines(item),
    narrow: item.lanes > 1,
    style: {
      top: top(item.start),
      height: `max(${span(item.start, item.end)}, 1.5rem)`,
      left: `calc(${(item.lane / item.lanes) * 100}% + 2px)`,
      width: `calc(${100 / item.lanes}% - 4px)`,
      borderLeftColor: item.color,
      backgroundColor: `${item.color}1f`,
    },
  }));

const minuteAt = event => {
  const rect = event.currentTarget.getBoundingClientRect();
  return props.startMinute + Math.floor((event.clientY - rect.top) / PX_PER_MINUTE);
};

const slotAt = (column, event) =>
  snapMinute(minuteAt(event), column.snapPeriods, column.duration);

// Horário que já começou (ou dia que já passou) não abre marcação.
const isPast = (column, minute) => minute < (column.pastUntil || 0);

const onMove = (column, event) => {
  const minute = slotAt(column, event);
  hover.value = isPast(column, minute) ? null : { key: column.key, minute };
};

const onClick = (column, event) => {
  const minute = slotAt(column, event);
  if (!isPast(column, minute)) emit('selectSlot', { column, minute });
};

const appointmentTitle = appointment =>
  [
    `${minutesToTime(appointment.start)}–${minutesToTime(appointment.end)}`,
    appointment.patient_name,
    appointment.professional_name,
    appointment.insurance,
    appointment.source === 'ai' ? t('AGENDA.PAGE.LEGEND_AI') : null,
  ]
    .filter(Boolean)
    .join(' · ');
</script>

<template>
  <div
    class="overflow-x-auto rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
    data-testid="agenda-calendar"
  >
    <div class="flex min-w-fit">
      <!-- Horas -->
      <div class="sticky left-0 z-20 w-14 shrink-0 bg-n-solid-1">
        <div class="h-14 border-b border-n-weak" />
        <div class="relative" :style="{ height: `${height}px` }">
          <span
            v-for="minute in hours"
            :key="minute"
            class="absolute right-2 -translate-y-1/2 text-label-small text-n-slate-10 tabular-nums"
            :style="{ top: top(minute) }"
          >
            {{ minute === startMinute ? '' : minutesToTime(minute) }}
          </span>
        </div>
      </div>

      <div
        v-for="column in columns"
        :key="column.key"
        class="flex-1 min-w-0 border-l border-n-weak"
        :style="{ minWidth: columnMinWidth }"
      >
        <div
          class="sticky top-0 z-10 flex flex-col justify-center h-14 px-3 border-b border-n-weak bg-n-solid-1"
          :class="{ 'bg-n-brand/5': column.date === today && !column.isProfessional }"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span
              v-if="column.color"
              class="size-2.5 rounded-full shrink-0"
              :style="{ backgroundColor: column.color }"
            />
            <span
              class="text-heading-3 truncate"
              :class="column.date === today && !column.isProfessional ? 'text-n-brand' : 'text-n-slate-12'"
            >
              {{ column.title }}
            </span>
          </div>
          <span class="text-label-small text-n-slate-11 truncate">
            {{ column.subtitle }}
          </span>
        </div>

        <div
          class="relative bg-n-slate-2"
          :class="column.pastUntil >= endMinute ? 'cursor-default' : 'cursor-pointer'"
          :style="{ height: `${height}px` }"
          :data-testid="`agenda-column-${column.key}`"
          @mousemove="onMove(column, $event)"
          @mouseleave="hover = null"
          @click="onClick(column, $event)"
        >
          <!-- Períodos de atendimento -->
          <div
            v-for="(period, index) in column.periods"
            :key="`p-${index}`"
            class="absolute inset-x-0 bg-n-solid-1"
            :style="{ top: top(period.start), height: span(period.start, period.end) }"
          />
          <!-- Horário que já passou -->
          <div
            v-if="column.pastUntil > startMinute"
            class="absolute inset-x-0 top-0 pointer-events-none agenda-past"
            :style="{ height: span(startMinute, column.pastUntil) }"
            :data-testid="`agenda-past-${column.key}`"
          />
          <!-- Linhas das horas -->
          <div
            v-for="minute in hours"
            :key="`h-${minute}`"
            class="absolute inset-x-0 border-t border-n-weak pointer-events-none"
            :style="{ top: top(minute) }"
          />
          <!-- Bloqueios -->
          <div
            v-for="block in column.blocks"
            :key="`b-${block.id}`"
            class="absolute inset-x-0 flex items-start px-2 py-1 overflow-hidden pointer-events-none agenda-blocked"
            :style="{ top: top(block.start), height: span(block.start, block.end) }"
          >
            <span class="text-label-small text-n-slate-11 truncate">
              {{ block.reason || t('AGENDA.PAGE.LEGEND_BLOCKED') }}
            </span>
          </div>
          <!-- Horário sob o mouse -->
          <div
            v-if="hover && hover.key === column.key"
            class="absolute inset-x-1 flex items-start px-2 py-0.5 rounded-md outline-1 outline-dashed outline-n-brand/60 bg-n-brand/5 pointer-events-none"
            :style="{
              top: top(hover.minute),
              height: `${(column.duration || 15) * PX_PER_MINUTE}px`,
            }"
          >
            <span class="text-label-small text-n-brand">
              + {{ minutesToTime(hover.minute) }}
            </span>
          </div>
          <!-- Agora -->
          <div
            v-if="column.date === today && nowMinute >= startMinute && nowMinute <= endMinute"
            class="absolute inset-x-0 z-[5] h-0.5 bg-n-ruby-9 pointer-events-none"
            :style="{ top: top(nowMinute) }"
          />
          <!-- Consultas -->
          <button
            v-for="appointment in laidOut(column)"
            :key="appointment.id"
            type="button"
            class="absolute z-[6] flex flex-col items-start px-2 overflow-hidden text-left border-l-[3px] rounded-md hover:brightness-95 hover:z-[7] focus:outline focus:outline-2 focus:outline-n-brand"
            :class="appointment.lines === 1 ? 'justify-center' : 'py-1'"
            :style="appointment.style"
            :title="appointmentTitle(appointment)"
            :data-testid="`agenda-appointment-${appointment.id}`"
            @click.stop="emit('openAppointment', appointment)"
            @mousemove.stop
          >
            <!-- Linha 1: horário (+ nome nos cartões curtos) -->
            <span class="flex items-center w-full min-w-0 gap-1 leading-4">
              <span class="text-label-small text-n-slate-11 tabular-nums shrink-0">
                {{ minutesToTime(appointment.start) }}
              </span>
              <span
                v-if="appointment.source === 'ai'"
                class="i-lucide-sparkles size-3 text-n-brand shrink-0"
              />
              <span
                v-if="appointment.lines === 1 || (appointment.lines === 2 && !appointment.narrow)"
                class="text-sm font-medium truncate text-n-slate-12"
              >
                {{ appointment.patient_name }}
              </span>
            </span>
            <!-- Cartão estreito (dividindo o horário): o paciente vale mais que o profissional, que já está na cor. -->
            <span
              v-if="appointment.lines === 3 || (appointment.lines === 2 && appointment.narrow)"
              class="w-full text-sm font-medium leading-5 truncate text-n-slate-12"
            >
              {{ appointment.patient_name }}
            </span>
            <span
              v-if="appointment.lines === 3 || (appointment.lines === 2 && !appointment.narrow)"
              class="w-full leading-4 text-label-small truncate text-n-slate-11"
            >
              {{
                showProfessionalName
                  ? appointment.professional_name
                  : appointment.insurance
              }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.agenda-past {
  background-color: rgb(var(--slate-3) / 0.75);
}

.agenda-blocked {
  background-image: repeating-linear-gradient(
    -45deg,
    rgb(var(--slate-4) / 0.9) 0 6px,
    rgb(var(--slate-3) / 0.9) 6px 12px
  );
}
</style>
